// PURE shop-card mini-FSM (IMPLEMENTATION §2.8 step 1–3, §2.7 "Shop axis lock").
// Until `lockPx` of travel the card does nothing (and never preventDefaults), so the browser can pan the
// `touch-action: pan-x` row. At the lock: |dy| > |dx| and dy < 0 → adopt (drag out); otherwise → scroll.

export type CardState =
  | { k: 'idle' }
  | { k: 'pending'; id: number; x0: number; y0: number; mouse: boolean }
  | { k: 'scroll'; id: number; x0: number; y0: number; mouse: boolean }
  | { k: 'adopted'; id: number };

/**
 * - `adopt`: hand the pointer to the input root (adoptPointer).
 * - `scroll`: horizontal browse. Touch: the browser pans natively; mouse: the card scrolls the row by dx.
 * - `tap`: released before the lock (M6: detail pop-over with Place).
 * - `reset`: gesture aborted (pointercancel, palm guard).
 */
export type CardAction = 'none' | 'adopt' | 'scroll' | 'tap' | 'reset';

export interface CardStep {
  state: CardState;
  action: CardAction;
}

export const CARD_IDLE: CardState = { k: 'idle' };

export function cardDown(s: CardState, id: number, x: number, y: number, mouse: boolean): CardStep {
  if (s.k === 'idle') return { state: { k: 'pending', id, x0: x, y0: y, mouse }, action: 'none' };
  // Palm guard: a second pointer during a pending press cancels it; during a scroll/drag it is ignored.
  if (s.k === 'pending' && s.id !== id) return { state: CARD_IDLE, action: 'reset' };
  return { state: s, action: 'none' };
}

export function cardMove(s: CardState, id: number, x: number, y: number, lockPx: number): CardStep {
  if (s.k === 'pending' && s.id === id) {
    const dx = x - s.x0;
    const dy = y - s.y0;
    if (dx * dx + dy * dy < lockPx * lockPx) return { state: s, action: 'none' };
    if (Math.abs(dy) > Math.abs(dx) && dy < 0)
      return { state: { k: 'adopted', id }, action: 'adopt' };
    return { state: { k: 'scroll', id, x0: s.x0, y0: s.y0, mouse: s.mouse }, action: 'scroll' };
  }
  if (s.k === 'scroll' && s.id === id) return { state: s, action: s.mouse ? 'scroll' : 'none' };
  return { state: s, action: 'none' };
}

export function cardUp(s: CardState, id: number): CardStep {
  if (s.k === 'idle' || s.id !== id) return { state: s, action: 'none' };
  return { state: CARD_IDLE, action: s.k === 'pending' ? 'tap' : 'none' };
}

export function cardCancel(s: CardState, id: number): CardStep {
  if (s.k === 'idle' || s.id !== id) return { state: s, action: 'none' };
  return { state: CARD_IDLE, action: 'reset' };
}
